import { lazy, Suspense, useMemo, useState } from 'react'

import ChatIcon from '@heroicons/react/outline/esm/ChatIcon.js'
import ShareIcon from '@heroicons/react/outline/esm/ShareIcon.js'
import { clsx } from 'clsx'

import { NetworkVisibility, Post } from '@tribeplatform/gql-client/types'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'

import { useI18n } from '../i18n/providers/I18nProvider.js'
import { useWebShare } from '../utils/react-use-web-share.js'
import { useMobileDetect } from '../utils/use-mobile-detect-hook.js'
import { FollowAction } from './Actions/FollowAction.js'
import { LikeAction } from './Actions/LikeAction.js'

const ShareModal = lazy(() =>
  import('../ShareModal/ShareModal.js').then(module => ({
    default: module.ShareModal,
  })),
)

interface Props {
  post: Post
  actions?: string[]
  isLastChild?: boolean
  style?: React.HTMLAttributes<HTMLDivElement>['style']
  showIcon?: boolean
  showText?: boolean
  fullWidth?: boolean
}
export const PostActionBar = ({
  post,
  actions,
  isLastChild = false,
  style,
  showIcon = true,
  showText = true,
  fullWidth = true,
}: Props) => {
  const intl = useI18n()
  if (!actions) {
    actions = ['like', 'follow', 'share']
  }

  const { data: network } = useNetwork()
  const [shareModal, setShareModal] = useState(false)
  const { isSupported, share } = useWebShare()
  const detectMobile = useMobileDetect()

  const isPrivate =
    post?.space?.private || network?.visibility === NetworkVisibility.private
  const postUrl = post?.url

  const allActions = useMemo(
    () => ({
      like: {
        Component: LikeAction,
      },
      reply: {
        icon: ChatIcon,
        text: intl.$t({
          id: 'Generics.Reply',
          defaultMessage: 'Reply',
        }),
      },
      follow: {
        Component: FollowAction,
      },
      share: {
        icon: ShareIcon,
        text: intl.$t({
          id: 'Generics.Share',
          defaultMessage: 'Share',
        }),
        action: () => {
          if (!isPrivate && isSupported && detectMobile.isMobile()) {
            share({ title: post.title, url: postUrl })
          } else {
            setShareModal(true)
          }
        },
      },
    }),
    [intl, isPrivate, isSupported, detectMobile, share, post.title, postUrl],
  )

  return (
    <>
      <Card.Content
        className={clsx(
          'flex space-s-4 py-0 sm:p-1 sm:px-6 min-w-0',
          isLastChild && 'pb-3',
        )}
        style={style}
      >
        {actions.map(key => {
          const action = allActions[key]
          if (!action) {
            return null
          }

          if (action.Component) {
            return (
              <div className={clsx('min-w-0', fullWidth && 'flex-1')} key={key}>
                <action.Component
                  post={post}
                  showIcon={showIcon}
                  showText={showText}
                />
              </div>
            )
          }

          return (
            <div
              className={clsx('flex min-w-0', fullWidth && 'flex-1')}
              key={key}
            >
              <Button
                variant="outline"
                onClick={action.action}
                className="group select-none"
                size="md"
                fullWidth
                leadingIcon={
                  showIcon ? (
                    <action.icon className="w-6 h-6 transition-transform group-hover:-rotate-12 shrink-0" />
                  ) : null
                }
              >
                {showText && <span className="truncate">{action.text}</span>}
              </Button>
            </div>
          )
        })}
      </Card.Content>
      {shareModal && (
        <Suspense fallback={<></>}>
          <ShareModal
            isPrivate={isPrivate}
            open
            onClose={() => setShareModal(false)}
            title={post.title}
            postId={post.id}
            url={postUrl}
          />
        </Suspense>
      )}
    </>
  )
}
